import { isBefore, parseISO } from 'date-fns';
import isAfter from 'date-fns/isAfter';
import isValid from 'date-fns/isValid';
import isEqual from 'fast-deep-equal';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import * as Yup from 'yup';

import type { FolderOrItem } from 'components/FolderStructure/types';
import { formatStandard, parseAndFormatDateTime } from 'libs/dateFunctions';

export enum MessageType {
  InfoPage = 10,
  InfoMessage = 20,
}

export enum VisibilityType {
  Public = 10,
  IncludeSubcontractors = 20,
  Employees = 30,
  EmployeesInCompanyStructure = 32,
  SpecificEmployees = 35,
  SpecificCompaniesOnly = 37,
  UserGroup = 38,
  NotVisible = 40,
}

export enum MessageView {
  App = 10,
  Web = 20,
  AdminWeb = 30,
}

export interface InfoCenterFeedback {
  id: string;
  companyId: string;
  avatarImageId: string;
  likeCount: number;
  createdDateTime: string;
  modifiedDateTime: string;
  companyName: string;
  userName: string;
  text: string;
  avatarSrcSm: string;
  avatarSrcM: string;
  avatarSrcLg: string;
  collectionRef: string;
  idRef: string;
  userId: string;
  userCompanyProfileId: string;
}

export interface InfoItem {
  id: string;
  companyId: string;
  companyIds: string[];
  companyName: string;
  createdAt: string;
  createdByUserId: string | null;
  createdByUserName: string | null;
  messageType: MessageType;
  messageVisibilityLevel: VisibilityType;
  modifiedAt: string;
  modifiedByUserId: string;
  modifiedByUserName: string | null;
  title: string;
  alertText: string | null; // Banner
  workDescription: SlateNode[]; // slate types shape
  workDescriptionLegacy: string | null; // maybe {} shape
  projectIds: string[];
  pushNotificationLog: unknown[];
  tagIds: string[];
  userIds: string[];
  userCompanyProfileIds?: string[];
  usersSentTo: number;
  sortOrder: number;
  infoPageFiles: {
    fileReferenceId: string;
    name: string;
    extension?: string;
  }[];
  isSysAdminMessage: boolean; // no idea
  isEditable: boolean;
  // following fields were specific to messages before folders were introduced
  startDate: string | null;
  finishedDate: string | null;
  feedbackAllowMultiple: boolean;
  feedbackOptions: string[];
  feedbackRequested: boolean;
  feedbackRequestedDitio: boolean;
  feedback: InfoCenterFeedback[];
  lastPushByUser: string | null;
  lastPushByUserName: string | null;
  lastInitiatedDateTime: string | null;
  lastPushSendDateTime: string | null;
  nps: boolean;
  visibleOnMobileApp: boolean;
  visibleForCompanyIds: string[];
  userGroupId: string | null;
}

export interface NewInfoPageFolder
  extends Pick<
    InfoItem,
    | 'projectIds'
    | 'title'
    | 'messageType'
    | 'messageVisibilityLevel'
    | 'sortOrder'
    | 'userGroupId'
    | 'userIds'
    | 'visibleForCompanyIds'
    | 'tagIds'
  > {
  isFolder: true;
  parentFolderId: string | null;
}

export const isInfoMessage = (infoItem: InfoItem) =>
  infoItem.messageType === MessageType.InfoMessage;

export interface InfoItemWithDate
  extends Omit<InfoItem, 'createdAt' | 'modifiedAt'> {
  createdAt: Date | null;
  modifiedAt: Date | null;
  hidden: boolean;
  companyName: string;
}

type SlateType = 'paragraph' | string;

export type SlateChild = {
  text: string;
  type: string | null;
  children?: SlateChild[];
  url?: string | null;
  fileReferenceId?: string | null;
  bold?: boolean | null;
  italic?: boolean | null;
  underline?: boolean | null;
};
export interface SlateNode {
  type: SlateType;
  fileReferenceId?: string | null;
  url?: string | null;
  children: SlateChild[];
}

const { Public, ...pageVisibilityLevel } = VisibilityType;

export const useMakeInfoItemValidationSchema = (isMessage: boolean) => {
  const { t } = useTranslation();
  const required = t('ValidationError.Required');
  const dateType = t('ValidationError.Date_TypeError');

  const pageValidationSchema = Yup.object().shape({
    title: Yup.string().required(required),
    workDescription: Yup.array().of(Yup.object().shape({})).required(), // check shape
    messageVisibilityLevel: Yup.mixed()
      .oneOf(
        Object.values(pageVisibilityLevel),
        t('InfoCenter.Errors.SelectOptions')
      )
      .required(required),
    tagIds: Yup.array().of(Yup.string()),
    user: Yup.array()
      .of(Yup.object())
      .when('messageVisibilityLevel', {
        is: (value) => value === pageVisibilityLevel.SpecificEmployees,
        then: (schema) => schema.min(1, required).required(required),
        otherwise: (schema) => schema,
      }),
    visibleForCompany: Yup.mixed().when('messageVisibilityLevel', {
      is: (value) => value === VisibilityType.SpecificCompaniesOnly,
      then: () => Yup.array().min(1, required).required(required),
    }),
    projIds: Yup.array().of(Yup.string()),
  });

  const additionalMessageSchema = Yup.object().shape({
    messageVisibilityLevel: Yup.mixed()
      .oneOf(
        Object.values(VisibilityType),
        t('InfoCenter.Errors.SelectOptions')
      )
      .required(required),
    userGroupId: Yup.string().when('messageVisibilityLevel', {
      is: (value) => value === VisibilityType.UserGroup,
      then: (schema) => schema.required(required),
      otherwise: (schema) => schema.nullable(),
    }),
    visibleOnMobileApp: Yup.bool(),
    startDate: Yup.date().nullable().typeError(dateType),
    finishedDate: Yup.date()
      .nullable()
      .typeError(dateType)
      .when('startDate', {
        is: (startDate) => startDate && isValid(new Date(startDate)),
        then: (schema) =>
          schema.test(
            'isBefore',
            t('ValidationError.EndDateEarly'),
            (endDate, context) =>
              !endDate || isBefore(context.parent.startDate as Date, endDate)
          ),
      }),
    feedbackRequested: Yup.bool(),
    feedbackOptions: Yup.mixed().when('feedbackRequested', {
      is: (requested) => !!requested,
      then: () =>
        Yup.array()
          .of(Yup.string())
          .test(
            'unique-options',
            t('ValidationError.UniqueOptions'),
            (arr) => new Set(arr).size === (arr as string[]).length
          )
          .required(required),
      otherwise: () => Yup.array().of(Yup.string()),
    }),
    alertText: Yup.string().nullable(),
  });

  return isMessage
    ? pageValidationSchema.concat(additionalMessageSchema)
    : pageValidationSchema;
};

export const getDateCaption = (message: InfoItemWithDate) => {
  if (message.modifiedAt) {
    return i18n.t('InfoCenter.Common.ModifiedAt', {
      date: formatStandard(message.modifiedAt),
    });
  }

  return i18n.t('InfoCenter.Common.CreatedAt', {
    date: formatStandard(message.createdAt),
  });
};

export const getISOParsedDateCaption = (message: InfoItem | FolderOrItem) => {
  if (message.modifiedAt) {
    return i18n.t('InfoCenter.Common.ModifiedAt', {
      date: formatStandard(parseISO(message.modifiedAt?.toString())),
    });
  }

  return i18n.t('InfoCenter.Common.CreatedAt', {
    date: formatStandard(parseISO(message.createdAt?.toString())),
  });
};

export const getVisibilityCaption = (
  message: InfoItem | InfoItemWithDate | FolderOrItem
) => {
  const companyName = !message.isEditable ? ` (${message.companyName})` : '';

  let caption = '';

  switch (message.messageVisibilityLevel) {
    case VisibilityType.NotVisible:
      caption = i18n.t('InfoCenter.Form.VisibilityHidden');
      break;
    case VisibilityType.Employees:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t('InfoCenter.Form.VisibilityEmployees'),
      });
      break;
    case VisibilityType.IncludeSubcontractors:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t('InfoCenter.Form.VisibilityWithUE'),
      });
      break;
    case VisibilityType.Public:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t('InfoCenter.Form.VisibilitySysOp'),
      });
      break;
    case VisibilityType.SpecificEmployees:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t('InfoCenter.Form.VisibilitySpecificEmployees'),
      });
      break;
    case VisibilityType.EmployeesInCompanyStructure:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t(
          'InfoCenter.Form.VisibilityEmployeesInCompanyStructure'
        ),
      });
      break;
    case VisibilityType.SpecificCompaniesOnly:
      caption = i18n.t('InfoCenter.Common.VisibleFor', {
        userGroup: i18n.t('InfoCenter.Form.VisibilitySpecificCompaniesOnly'),
      });
      break;
    default:
      break;
  }

  return caption + companyName;
};

export const getLastSentCaption = (
  t: TFunction,
  message: InfoItem,
  userName: string
) => {
  if (!message || !isInfoMessage(message)) return null;

  if (
    message.startDate &&
    message.lastInitiatedDateTime &&
    message.lastPushSendDateTime &&
    isAfter(parseISO(message.lastPushSendDateTime), new Date()) &&
    message.lastInitiatedDateTime !== message.lastPushSendDateTime
  ) {
    return message.messageType === MessageType.InfoPage
      ? t('InfoCenter.PagesPushDialog.AlreadyScheduled')
      : t('InfoCenter.PushDialog.AlreadyScheduled');
  }

  if (message.lastPushSendDateTime) {
    return t('InfoCenter.Common.LastSentPush', {
      date: parseAndFormatDateTime({
        value: message.lastPushSendDateTime,
        format: 'HH:mm PP',
      }),
      user: userName,
    });
  }

  return null;
};

export const defaultWorkDescription: SlateNode[] = [
  {
    type: 'paragraph',
    children: [
      {
        text: '',
        type: null,
      },
    ],
  },
];

const emptiedWorkDescription = [
  {
    type: 'paragraph',
    fileReferenceId: null,
    url: null,
    children: [
      {
        text: '',
        type: null,
        children: null,
        url: null,
        fileReferenceId: null,
      },
    ],
  },
];

export const isWorkDescriptionNotSet = (data: SlateNode[]) =>
  !data.length ||
  isEqual(data, defaultWorkDescription) ||
  isEqual(data, emptiedWorkDescription);

export const modifyItemDatesToString = (item: InfoItemWithDate) => ({
  ...item,
  createdAt: item.createdAt.toISOString(),
  modifiedAt: item.modifiedAt.toISOString(),
});

export interface User {
  id: string;
  companyProfileId: string;
  companyId: string;
  pieceworkSetupAllowed: boolean;
  companyIdMain: null | any; // clarify
  connectedCompanyId: string;
  companyName: string;
  companyNameMain: null | any;
  connectedCompanyName: string;
  currentToken: string | null;
  employeeNumber: string; // should be enum
  cardId: string;
  cardExpirationDate: string;
  userName: string;
  userGroupId: string; // enum
  userSecurityLevel: number; // enum
  userGroupName: null;
  email: string;
  name: string;
  mobilePhone: string;
  active: boolean;
  hasTransaction: boolean;
  birthDate: string; // dd.mm.yyyy
  avatarSrc: string;
  avatarImageId: string;
}

export const getTwoDigitFloatFromResult = (int: number): number =>
  parseFloat(int.toFixed(2));
